import {
  API_ENDPOINT,
  authHeaders,
  fetchWithAuth,
  parseApiResponse,
  setAuthToken,
} from ".";
import { User } from "../types/user";

interface AuthResponse {
  token: string;
  user: User;
}

type ApiResult<TExtra = {}> = (
  | { ok: true; status: number }
  | { ok: false; status: number; message: string }
) &
  Partial<TExtra>;

async function postJson<TExtra extends object = {}>(
  path: string,
  body: unknown,
  init?: RequestInit,
): Promise<ApiResult<TExtra> & { data?: unknown }> {
  try {
    const response = await fetch(`${API_ENDPOINT}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      return {
        ok: false,
        status: response.status,
        message: errorBody.error?.message ?? "Request failed",
      };
    }
    const data = await response.json();
    return { ok: true, status: response.status, data: data.data };
  } catch (err) {
    console.error(err);
    return {
      ok: false,
      status: 0,
      message: "Network error. Please try again.",
    };
  }
}

// ─── Login / Register / Session ─────────────────────────────────────────────

export const loginUserAPI = async (obj: {
  email: string;
  password: string;
}) => {
  try {
    const response = await fetch(`${API_ENDPOINT}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: obj.email, password: obj.password }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        status: response.status,
        message: body.error?.message ?? "Login failed",
      };
    }

    const data = await parseApiResponse<AuthResponse>(response);
    setAuthToken(data.token);
    return { ok: true, status: 200, user: data.user, token: data.token };
  } catch (err) {
    console.error(err);
    return { ok: false, status: 500, message: "Network error. Please try again." };
  }
};

export const registerUserAPI = async (obj: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  title: string;
  organisation: string;
  position: string;
  country: string;
}) => {
  try {
    const response = await fetch(`${API_ENDPOINT}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(obj),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        status: response.status,
        message: body.error?.message ?? "Registration failed",
      };
    }

    const data = await parseApiResponse<AuthResponse>(response);
    setAuthToken(data.token);
    return { ok: true, status: 201, user: data.user, token: data.token };
  } catch (err) {
    console.error(err);
    return {
      ok: false,
      status: 500,
      message: "Network error. Please try again.",
    };
  }
};

export const logoutUserAPI = async () => {
  setAuthToken(null);
  return { ok: true };
};

/**
 * Returns the raw fetch Response so callers can distinguish 401 (token
 * rejected) from network failure (Response is undefined).
 */
export const checkUserSession = async (): Promise<Response | undefined> => {
  const token = localStorage.getItem("eventshouse_auth_token");
  if (!token) {
    return undefined;
  }

  try {
    return await fetch(`${API_ENDPOINT}/auth/me`, {
      method: "GET",
      headers: authHeaders(),
    });
  } catch (err) {
    console.error(err);
    return undefined;
  }
};

export const updateUserAPI = async (obj: {
  title: string;
  organisation: string;
  position: string;
  country: string;
}) => {
  try {
    const response = await fetchWithAuth(`${API_ENDPOINT}/auth/me`, {
      method: "PATCH",
      body: JSON.stringify(obj),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false as const,
        status: response.status,
        message: body.error?.message ?? "Failed to update profile",
      };
    }
    const data = await parseApiResponse<{ user: User }>(response);
    return { ok: true as const, status: 200, user: data.user };
  } catch (err) {
    console.error(err);
    return {
      ok: false as const,
      status: 0,
      message: "Network error. Please try again.",
    };
  }
};

// ─── Email verification (signup) ────────────────────────────────────────────

export const sendEmailCodeAPI = async (obj: { email: string }) => {
  return postJson("/auth/send-verification-code", obj);
};

export const verifyEmailCodeAPI = async (obj: {
  email: string;
  code: string;
}) => {
  return postJson("/auth/verify-email-code", obj);
};

// ─── Password reset ─────────────────────────────────────────────────────────

export const sendPasswordCodeAPI = async (obj: { email: string }) => {
  return postJson("/auth/forgot-password", obj);
};

export const verifyPasswordCodeAPI = async (obj: {
  email: string;
  code: string;
}) => {
  return postJson("/auth/verify-reset-code", obj);
};

export const resetPasswordAPI = async (obj: {
  email: string;
  code: string;
  newPassword: string;
}) => {
  return postJson("/auth/reset-password", obj);
};

// ─── Account soft-delete ────────────────────────────────────────────────────

export async function deleteAccountAPI(obj: {
  password: string;
  reason?: string;
}): Promise<{ ok: boolean; status: number; message?: string }> {
  try {
    const response = await fetchWithAuth(`${API_ENDPOINT}/auth/me`, {
      method: "DELETE",
      body: JSON.stringify(obj),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        status: response.status,
        message: body.error?.message ?? "Failed to delete account",
      };
    }
    return { ok: true, status: response.status };
  } catch (err) {
    console.error(err);
    return { ok: false, status: 0, message: "Network error. Please try again." };
  }
}

// ─── GDPR data export ───────────────────────────────────────────────────────

export async function exportMyDataAPI(): Promise<Blob> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/auth/me/export`, {
    method: "GET",
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error?.message ?? "Failed to export account data");
  }
  return response.blob();
}
